import {
    Alert,
    Badge,
    Button,
    CopyButton,
    Divider,
    Group,
    Modal,
    Radio,
    SegmentedControl,
    Skeleton,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconCopy, IconLink } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDate } from './format';
import { type Recording, recordingsApi, type RecordingShare, type ShareAudience } from './recordings-api';
import { i18n } from '@/i18n';
import { notifyError } from '@/shared/notify';

const EXPIRY_OPTIONS = ['7', '30', 'never'] as const;
type Expiry = (typeof EXPIRY_OPTIONS)[number];

interface ShareModalProps {
    recording: Recording;
    publicLinksEnabled: boolean;
    onClose: () => void;
}

export function ShareModal({ recording, publicLinksEnabled, onClose }: Readonly<ShareModalProps>) {
    const { t } = useTranslation('recordings');
    const language = i18n.language;
    const [audience, setAudience] = useState<ShareAudience>('organization');
    const [expiry, setExpiry] = useState<Expiry>('7');
    const [shares, setShares] = useState<RecordingShare[] | null>(null);
    const [createdUrl, setCreatedUrl] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);
    const [revokingId, setRevokingId] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        recordingsApi
            .shares(recording.id)
            .then((loaded) => {
                if (!cancelled) setShares(loaded);
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                setShares([]);
                notifyError(t('errors.shares'), error);
            });
        return () => {
            cancelled = true;
        };
    }, [recording.id, t]);

    const create = async () => {
        setCreating(true);
        try {
            const { share, url } = await recordingsApi.createShare(recording.id, {
                audience,
                expiresInDays: expiry === 'never' ? null : Number(expiry),
            });
            setCreatedUrl(new URL(url, globalThis.location.origin).toString());
            setShares((current) => [share, ...(current ?? [])]);
        } catch (error) {
            notifyError(t('errors.shareCreate'), error);
        } finally {
            setCreating(false);
        }
    };

    const revoke = async (share: RecordingShare) => {
        setRevokingId(share.id);
        try {
            await recordingsApi.revokeShare(recording.id, share.id);
            setShares((current) => current?.filter((item) => item.id !== share.id) ?? null);
            notifications.show({ message: t('shareModal.revoked') });
        } catch (error) {
            notifyError(t('errors.shareRevoke'), error);
        } finally {
            setRevokingId(null);
        }
    };

    return (
        <Modal opened size="lg" title={t('shareModal.title')} onClose={onClose}>
            <Stack gap="lg">
                <Text c="dimmed" size="sm">
                    {recording.roomName} · {formatDate(recording.startedAt, language)}
                </Text>

                {createdUrl && (
                    <Alert color="warning" icon={<IconLink size={20} />} title={t('shareModal.createdTitle')} variant="light">
                        <Stack gap="sm">
                            <Text size="sm">{t('shareModal.createdOnce')}</Text>
                            <Group gap="sm" wrap="nowrap">
                                <TextInput
                                    readOnly
                                    aria-label={t('shareModal.createdTitle')}
                                    flex={1}
                                    value={createdUrl}
                                    onFocus={(event) => {
                                        event.currentTarget.select();
                                    }}
                                />
                                <CopyButton value={createdUrl}>
                                    {({ copied, copy }) => (
                                        <Button
                                            color={copied ? 'success' : undefined}
                                            leftSection={copied ? <IconCheck size={20} /> : <IconCopy size={20} />}
                                            onClick={copy}
                                        >
                                            {copied ? t('shareModal.copied') : t('shareModal.copy')}
                                        </Button>
                                    )}
                                </CopyButton>
                            </Group>
                        </Stack>
                    </Alert>
                )}

                <Radio.Group
                    label={t('shareModal.audience')}
                    value={audience}
                    onChange={(value) => {
                        setAudience(value === 'public' ? 'public' : 'organization');
                    }}
                >
                    <Stack gap="sm" mt="xs">
                        <Radio
                            description={t('shareModal.audienceOrganizationHint')}
                            label={t('shareModal.audienceOrganization')}
                            value="organization"
                        />
                        {publicLinksEnabled && (
                            <Radio
                                description={t('shareModal.audiencePublicHint')}
                                label={t('shareModal.audiencePublic')}
                                value="public"
                            />
                        )}
                    </Stack>
                </Radio.Group>

                <Stack gap="xs">
                    <Text fw={500} size="sm">
                        {t('shareModal.expiry')}
                    </Text>
                    <SegmentedControl
                        value={expiry}
                        data={[
                            { value: '7', label: t('shareModal.expiry7') },
                            { value: '30', label: t('shareModal.expiry30') },
                            { value: 'never', label: t('shareModal.expiryNever') },
                        ]}
                        onChange={(value) => {
                            setExpiry(EXPIRY_OPTIONS.find((option) => option === value) ?? '7');
                        }}
                    />
                </Stack>

                <Group justify="flex-end">
                    <Button leftSection={<IconLink size={20} />} loading={creating} onClick={() => void create()}>
                        {t('shareModal.create')}
                    </Button>
                </Group>

                <Divider />

                <Stack gap="sm">
                    <Text fw={500} size="sm">
                        {t('shareModal.active')}
                    </Text>
                    {shares === null && <Skeleton h="var(--spacing-10)" />}
                    {shares?.length === 0 && (
                        <Text c="dimmed" size="sm">
                            {t('shareModal.noActive')}
                        </Text>
                    )}
                    {shares?.map((share) => (
                        <Group key={share.id} justify="space-between" wrap="nowrap">
                            <Stack gap="var(--spacing-1)">
                                <Badge color={share.audience === 'public' ? 'orange' : 'blue'} variant="light">
                                    {share.audience === 'public'
                                        ? t('shareModal.audiencePublic')
                                        : t('shareModal.audienceOrganization')}
                                </Badge>
                                <Text c="dimmed" size="sm">
                                    {t('shareModal.createdAt', { date: formatDate(share.createdAt, language) })} ·{' '}
                                    {share.expiresAt
                                        ? t('shareModal.expiresAt', { date: formatDate(share.expiresAt, language) })
                                        : t('shareModal.noExpiry')}
                                </Text>
                            </Stack>
                            <Button
                                color="danger"
                                flex="none"
                                loading={revokingId === share.id}
                                size="sm"
                                variant="subtle"
                                onClick={() => void revoke(share)}
                            >
                                {t('shareModal.revoke')}
                            </Button>
                        </Group>
                    ))}
                </Stack>
            </Stack>
        </Modal>
    );
}
